import { Alert, Box, Button, Flex, Modal, SegmentedControl, Stack, Text, Title, Tooltip } from "@mantine/core";
import { ISpecificationTable } from "@mctypes/frontend.types";
import { IApp } from "@mctypes/global.types";
import { IUserPartial } from "@schemas/issueSchema";
import { IconInfoCircle, IconMailFilled, IconSearch } from "@tabler/icons-react";
import React, { useMemo } from "react";
import { STANDARD_COLORS } from "../../app/globalConstants";
import { doSendUserMessage } from "../../app/routes/admin/restQueries";
import { setShowSuccessMessageThunk } from "../../global/globalSlice";
import { useAppDispatch } from "../../hooks";
import { ChecklistDisciplineRoles } from "@mcapps/shared/enums";
import { MCApplicationsUrlEnum } from "@mcapps/shared/sharedConstants";
import ContactForm from "../ContactForm/ContactForm";
import ExcelImportValidatorWithSpecTable from "../ExcelImportValidator/ExcelImportValidatorWithSpecTable";
import HierarchyTreeSelector from "../HierarchyTreeSelector/HierarchyTreeSelector";
import McInputNew from "../McInput/McInputNew";
import SimpleStampCheck from "../SimpleStampCheck/SimpleStampCheck";
import UserAvatar from "../Avatar";
import "./TemplateSelector.css";

export type TemplateSelectorType = "MC" | "PROJECT" | "INSTANCE" | "EXCEL";

export interface ITemplate {
  _id: string;
  name: string;
  templateType: TemplateSelectorType;
  category?: string | null;
  [key: string]: any;
  description?: string;
  admin?: IUserPartial;
}

export interface IHierarchyCategory {
  _id: string;
  parentId: string;
  props: {
    templateId: string | null;
    title: string | null;
  };
}

const TemplateSelector: React.FC<{
  app: Pick<IApp, "icon" | "iconColor" | "name">;
  header: string;
  templates: ITemplate[];
  onCreate: (template: ITemplate) => void;
  closeModal: () => void;
  isTemplateProject: boolean;
  isTitleEmpty?: boolean;
  descriptionAndAdmin?: boolean;
  showMCButton?: boolean;
  showProjectButton?: boolean;
  showInstanceButton?: boolean;
  preSelectedTemplate?: ITemplate;
  disableOpprettButton?: boolean;
  createLoading?: boolean;
  onTemplateSelected?: (template: ITemplate) => void;
  excelImport: {
    enabled: boolean;
    specificationTable?: ISpecificationTable;
    onCreateFromExcel?: (data: string[][]) => void;
    zodValidation?: any;
  };
  bottomDescriptionSection?: React.ReactNode;
}> = ({
  app,
  header,
  children,
  templates,
  onCreate,
  closeModal,
  isTemplateProject,
  isTitleEmpty,
  descriptionAndAdmin,
  showMCButton = true,
  showProjectButton = true,
  showInstanceButton = true,
  preSelectedTemplate,
  disableOpprettButton,
  createLoading = false,
  onTemplateSelected,
  excelImport,
  bottomDescriptionSection,
}) => {
  const [filter, setFilter] = React.useState("");
  const [type, setType] = React.useState<TemplateSelectorType>("MC");
  const [selected, setSelected] = React.useState<ITemplate>(preSelectedTemplate);
  const [importedFormattedExcelDataItems, setImportedFormattedExcelDataItems] = React.useState<string[][]>(null);
  const [showContactForm, setShowContactForm] = React.useState<boolean>(false);
  const [copyIssuesAndTasks, setCopyIssuesAndTasks] = React.useState<boolean>(false);
  const [opprettWasClicked, setOpprettWasClicked] = React.useState<boolean>(false);

  const standardTemplateName = "Standard";
  const tomMalTemplateName = "Tom mal";

  const selectTemplate = (template: ITemplate) => {
    if (onTemplateSelected != null) {
      onTemplateSelected(template);
    }
    setSelected(template);
  };

  const disciplineMap = new Map<string, string>(
    app.name === MCApplicationsUrlEnum.CHECKLIST ? ChecklistDisciplineRoles.map((role) => [role.code, role.title]) : [],
  );
  const dispatch = useAppDispatch();
  const filteredTemplates: ITemplate[] = useMemo(() => {
    let filtered = [];

    if (type === "MC") {
      filtered = templates.filter((template) => template.templateType === "MC");
    }
    if (type === "INSTANCE") {
      filtered = templates.filter((template) => template.templateType === "INSTANCE");
    }
    if (type === "PROJECT") {
      filtered = templates.filter((template) => template.templateType === "PROJECT");
    }

    if (filter !== "") {
      filtered = filtered.filter((template) => template.name.toLowerCase().includes(filter.toLowerCase()));
    }

    return filtered;
  }, [type, templates, filter]);

  let categories: string[] = [];

  if (type === "MC" || type === "PROJECT") {
    categories = [
      ...new Set(filteredTemplates.map((template) => template.category).filter((category) => category != null)),
    ];
  }

  const parentNode: IHierarchyCategory = {
    _id: "main-parent",
    parentId: null,
    props: {
      templateId: "Maler",
      title: null,
    },
  };

  const groupNodes: IHierarchyCategory[] = categories
    .map((category, index) => {
      return {
        _id: `category-placeholder-${index}`,
        parentId: parentNode._id,
        props: {
          templateId: app.name === MCApplicationsUrlEnum.CHECKLIST ? disciplineMap.get(category) : category,
          title: null,
        },
      };
    })
    .sort((a, b) => {
      const aTitle = String(a.props.templateId || "").toLowerCase();
      const bTitle = String(b.props.templateId || "").toLowerCase();
      return aTitle.localeCompare(bTitle);
    });

  let templateNodes: IHierarchyCategory[] = filteredTemplates
    .map((template) => {
      if (template.category === null || template.category === "" || template.templateType === "INSTANCE") {
        return {
          _id: template._id,
          parentId: parentNode._id,
          props: {
            templateId: template.name,
            title: null,
          },
        };
      } else {
        let placeholder: IHierarchyCategory | undefined;

        if (app.name === MCApplicationsUrlEnum.CHECKLIST) {
          const catName = disciplineMap.get(template.category);
          placeholder = groupNodes?.find((ph) => ph.props.templateId === catName);
        } else {
          placeholder = groupNodes?.find((ph) => ph.props.templateId === template.category);
        }

        return {
          _id: template._id,
          parentId: placeholder?._id,
          props: {
            templateId: null,
            title: template.name,
          },
        };
      }
    })
    .sort((a, b) => {
      if (a.props.templateId === standardTemplateName || a.props.templateId === tomMalTemplateName) return -1;

      if (a.props.templateId != null && b.props.templateId != null) {
        return a.props.templateId.localeCompare(b.props.templateId);
      }

      if (a.props.title != null && b.props.title != null) {
        return a.props.title.toLowerCase().localeCompare(b.props.title.toLowerCase());
      }
      return 0;
    });

  const templateTreeNodes = [parentNode, ...groupNodes, ...templateNodes];

  const sortedFinalTemplates = templateTreeNodes.sort((a, b) => {
    const aIsMainParentChild = a.parentId === "main-parent";
    const bIsMainParentChild = b.parentId === "main-parent";
    const aIsParent = templateTreeNodes.some((template) => template.parentId === a._id);
    const bIsParent = templateTreeNodes.some((template) => template.parentId === b._id);

    if (aIsMainParentChild && !aIsParent && (!bIsMainParentChild || bIsParent)) return -1;
    if (bIsMainParentChild && !bIsParent && (!aIsMainParentChild || aIsParent)) return 1;

    if (aIsParent && !bIsParent) return 1;
    if (!aIsParent && bIsParent) return -1;

    return 0;
  });

  const findTemplateById = (templateId: any, templates: any[]): ITemplate | undefined => {
    return templates.find((template) => template._id === templateId);
  };

  const onSubmit = () => {
    setOpprettWasClicked(true);
    if (type === "EXCEL") {
      excelImport?.onCreateFromExcel(importedFormattedExcelDataItems);
    } else {
      onCreate({
        ...selected,
        _copyIssuesAndTasks: copyIssuesAndTasks,
      });
    }
  };

  const onTabChange = (tab: TemplateSelectorType) => {
    if (tab === "MC" && app.name === MCApplicationsUrlEnum.ISSUE) {
      selectTemplate(templates.find((template) => template.name === standardTemplateName));
    }
    if (tab === "MC" && app.name === MCApplicationsUrlEnum.CHECKLIST) {
      selectTemplate(templates.find((template) => template.name === tomMalTemplateName));
    }
    if (tab === "PROJECT") {
      selectTemplate(templates.find((template) => template.templateType === "PROJECT"));
    }
    if (tab === "INSTANCE") {
      selectTemplate(templates.find((template) => template.templateType === "INSTANCE"));
    }
    if (tab === "EXCEL") {
      selectTemplate(null);
    }
    setType(tab);
  };

  const isOpprettButtonDisabled =
    type === "EXCEL"
      ? importedFormattedExcelDataItems?.length === 0 || isTitleEmpty === true || disableOpprettButton === true
      : disableOpprettButton === true || selected == null;

  const send = async (message: string, subject: string) => {
    sendUserMessage(message, subject, () => setShowContactForm(false));
  };

  const sendUserMessage = async (message: string, subject: string, closeModal: () => void) => {
    try {
      await doSendUserMessage(subject, message);
      dispatch(setShowSuccessMessageThunk("Takk for tilbakemeldingen.\nVi vil komme tilbake til deg så snart vi kan."));
      closeModal();
    } catch (error) {
      dispatch(setShowSuccessMessageThunk("Noe gikk galt! Prøv å sende en e-post i stedet."));
    }
  };

  const importValidated = (data: string[][]) => {
    const hasTitleRow =
      Array.isArray(data) && data.length > 0 && data[0].every((cell) => Number.isNaN(Number(String(cell))));
    let dataWithTitleRow: string[][] =
      hasTitleRow === false && app.name === MCApplicationsUrlEnum.CHECKLIST ? [["nr", "navn"], ...data] : data;
    setImportedFormattedExcelDataItems(dataWithTitleRow);
  };

  return (
    <>
      <Modal
        styles={{
          content: { width: "min(90rem, calc(100vw - 2rem))" },
          body: { width: "100%", height: "40rem", minHeight: "30rem" },
        }}
        size={"auto"}
        opened={true}
        onClose={closeModal}
        title={header}
      >
        <Flex h="100%" p="xs" style={{ overflow: "auto" }}>
          <Flex direction="column" style={{ resize: "horizontal", minWidth: "22rem", overflow: "hidden" }}>
            <SegmentedControl
              color="mc-primary"
              mb="0.5rem"
              value={type}
              onChange={(value: TemplateSelectorType) => onTabChange(value)}
              data={[
                ...(showMCButton === true ? [{ label: "MC-maler", value: "MC" as TemplateSelectorType }] : []),
                ...(showProjectButton === true
                  ? [{ label: "Prosjektmaler", value: "PROJECT" as TemplateSelectorType }]
                  : []),
                ...(showInstanceButton === true
                  ? [{ label: "Kopier fra eksisterende", value: "INSTANCE" as TemplateSelectorType }]
                  : []),
                ...(excelImport.enabled === true ? [{ label: "Excel", value: "EXCEL" as TemplateSelectorType }] : []),
              ]}
              style={{ width: "100%" }}
            />
            {type !== "EXCEL" && (
              <>
                <div style={{ marginBottom: "0.5rem" }}>
                  <McInputNew
                    placeholder="Filtrer maler"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    rightSection={<IconSearch size={16} />}
                  />
                  {/* <SearchInput placeholder="Filtrer maler" value={filter} onChange={(e) => setFilter(e.target.value)} /> */}
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    background: "#FBFBFB",
                    height: "100%",
                    overflow: "auto",
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                    <Flex mb="0.25rem" style={{ height: "0px", flexGrow: 1, overflow: "hidden" }}>
                      <HierarchyTreeSelector
                        style={{
                          width: "352px",
                          maxWidth: "352px",
                          background: "#ededed",
                        }}
                        isVisible
                        nodeList={sortedFinalTemplates}
                        idProp={"templateId"}
                        titleProp={"title"}
                        selectedNodeIds={[selected?._id]}
                        barLabel="Maler"
                        hideLabelBar={true}
                        onSelectionChange={(node) => {
                          const selectedTemplate = findTemplateById(node[0]?.id, filteredTemplates);
                          selectTemplate(selectedTemplate);
                        }}
                      />
                    </Flex>
                    <Flex style={{ overflow: "hidden" }}>
                      <Button leftSection={<IconMailFilled />} onClick={() => setShowContactForm(true)} color="mc-blue">
                        Forslag til ny mal
                      </Button>
                    </Flex>
                  </div>
                </div>
              </>
            )}
            {type === "EXCEL" && excelImport?.specificationTable == null && (
              <>
                <Alert color="warning">Mangler spesifikasjonstabell for import</Alert>
              </>
            )}
            {type === "EXCEL" && importedFormattedExcelDataItems == null && excelImport?.specificationTable != null && (
              <ExcelImportValidatorWithSpecTable
                onValidated={(res) => importValidated(res.sourceData as string[][])}
                validators={excelImport?.zodValidation}
                skipTitleRow={true}
                specificationTable={excelImport?.specificationTable}
              />
            )}
            {type === "EXCEL" && importedFormattedExcelDataItems != null && (
              <Alert color="success">{importedFormattedExcelDataItems.length} rader importert fra excel</Alert>
            )}
          </Flex>
          {type === "MC" ? (
            <Stack bg="lightgray.0" ml="0.5rem" p="xs" gap="xs" style={{ flex: "0 0 350px" }}>
              {selected != null && descriptionAndAdmin === true && (
                <>
                  <Flex component="h5" justify="center" m={0} style={{ textAlign: "center" }}>
                    {selected.name}
                  </Flex>
                  <Flex direction="column" h="100%" justify="flex-end" style={{ flexGrow: 1, overflow: "auto" }}>
                    {selected.name !== standardTemplateName && selected.name !== tomMalTemplateName && (
                      <Stack gap={0} align="center" mb={8}>
                        <div style={{ marginRight: "-.5em" }}>
                          <UserAvatar id={selected.admin?._id} name={selected.admin?.name} size="xl" />
                          {/* <Avatar id={selected.admin?._id}  h="50px"/> */}
                        </div>
                        <Box component="h5" my="0.25rem">
                          {selected.admin?.name}
                        </Box>{" "}
                        <div
                          className="memberGroupBadge"
                          style={{ color: `${STANDARD_COLORS.orange}`, border: `3px solid ${STANDARD_COLORS.orange}` }}
                        >
                          ADMIN
                        </div>
                      </Stack>
                    )}
                    <Flex h="100%" p="xs" style={{ overflow: "auto" }}>
                      <Text size="sm">{selected.description}</Text>
                    </Flex>
                  </Flex>
                  {bottomDescriptionSection != null && <Box p="xs">{bottomDescriptionSection}</Box>}
                </>
              )}
            </Stack>
          ) : null}
          <Stack ml="0.5rem" style={{ flex: "1 1 0", minWidth: 0 }}>
            {children}
            <Stack flex={1} justify="space-between" mt="0.5rem">
              {isTemplateProject === false && type !== "EXCEL" && (
                <Stack px="md" pt="0.5rem">
                  <Stack gap={0}>
                    {(type === "MC" || type === "PROJECT") && (
                      <Text size="sm" fw="bold">
                        Valgt mal
                      </Text>
                    )}
                    {type === "INSTANCE" && (
                      <Text size="sm" fw="bold">
                        Kopier fra
                      </Text>
                    )}
                    <Text mb={0}>{selected?.name}</Text>
                  </Stack>
                  {type === "INSTANCE" && (
                    <Stack gap="xs">
                      <Title order={6}>Kopier innhold</Title>
                      <Flex gap="xs" wrap="wrap" align="center">
                        <Tooltip
                          label={
                            "Velg om kopien skal ta med alt som ligger i registeret, eller kun oppsettet (struktur og innstillinger)."
                          }
                          withArrow
                          zIndex={2042}
                        >
                          <Text c="var(--mantine-color-mc-primary-6)" style={{ cursor: "pointer" }}>
                            <IconInfoCircle size={20} />
                          </Text>
                        </Tooltip>
                        <SimpleStampCheck toggled={copyIssuesAndTasks} toggle={setCopyIssuesAndTasks} />
                        <Text>{copyIssuesAndTasks ? "Ta med innhold" : "Bare innstillinger"}</Text>
                      </Flex>
                      {copyIssuesAndTasks ? (
                        <Stack gap="xs">
                          <Text size="sm">
                            <Text fw={700}>Alt innhold blir kopiert</Text>- registeret kopieres med alle saker og
                            tilhørende aksjoner. Innstillinger og struktur følger også med.
                          </Text>
                          <Text size="sm" c="yellow.7">
                            <Text component="span" fw={700}>
                              OBS!
                            </Text>
                            Ansvarlig for utførelse og eier blir ikke kopiert.
                          </Text>
                        </Stack>
                      ) : (
                        <Text size="sm">
                          <Text fw={700}>Kun innstillinger kopieres</Text>- du får et nytt register med samme struktur
                          og konfigurasjon, men uten innhold (ingen poster/avvik eller oppgaver).
                        </Text>
                      )}
                    </Stack>
                  )}
                </Stack>
              )}
              <Stack align="center" justify="flex-end" flex={1}>
                <Button
                  disabled={isOpprettButtonDisabled || createLoading || opprettWasClicked}
                  loading={createLoading || opprettWasClicked}
                  size="md"
                  variant="filled"
                  onClick={() => onSubmit()}
                >
                  {isTemplateProject === false ? "Opprett" : "Opprett ny mal"}
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Flex>
      </Modal>
      <Modal size="lg" opened={showContactForm} onClose={() => setShowContactForm(false)} title="Kontakt">
        <ContactForm sendMessage={send} prefilledSubject={"Forslag til ny mal:"} />
      </Modal>
    </>
  );
};

export default TemplateSelector;
